import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, catchError, map, of, switchMap } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { ApiConfiguration } from "../../../services/generated/erp-api/api-configuration";
import { SalePointMenuDto } from "../../../services/generated/erp-api/models/sale-point-menu-dto";
import { WorkHour } from "../../../services/generated/waba-api/models/work-hour";
import { CompanyApiService } from "../../../services/generated/waba-api/services/company-api.service";
import { WorkHourApiService } from "../../../services/generated/waba-api/services/work-hour-api.service";
import { SILENT_HTTP_CONTEXT } from "../../../shared/utils/silent-http.util";
import { SalePointService } from "../../sale-points/sale-point.service";
import { nextOpening } from "./next-opening";

/** What the merchant chose in the "Épuisé aujourd'hui" dialog. */
export interface SoldOutChoice {
  /** When the dish comes back by itself; null = "until I turn it back on". */
  until: Date | null;
  /** Only this sales point; null = everywhere. */
  salePointReference: string | null;
}

/** Menu of a sales point with the dates the erp keeps per out-of-stock dish (not in the generated model yet). */
type MenuWithUntil = SalePointMenuDto & { outOfStockUntil?: Record<string, string> };

/**
 * "Épuisé aujourd'hui" of a restaurant dish: works out when the restaurant opens next (waba work hours) and saves
 * the choice in the erp, on the dish (everywhere) or on one sales point's menu. The erp puts the dish back in
 * stock by itself once the date has passed.
 */
@Injectable({ providedIn: "root" })
export class SoldOutService {
  constructor(
    private http: HttpClient,
    private erpConfig: ApiConfiguration,
    private companyApi: CompanyApiService,
    private workHourApi: WorkHourApiService,
    private salePoints: SalePointService,
  ) {}

  private headers() {
    return { "x-api-canal": "web", "x-api-requestId": uuidv4() };
  }

  /** Next opening of the restaurant; tomorrow at midnight when unknown. */
  nextOpening(now = new Date()): Observable<Date> {
    return this.workHours().pipe(
      catchError(() => of([])),
      map((hours) => nextOpening(hours, now)),
    );
  }

  /** Work hours of the restaurant's first WhatsApp number (empty when it has no number yet). */
  workHours(): Observable<WorkHour[]> {
    return this.companyApi.companyNumbersList(this.headers(), SILENT_HTTP_CONTEXT).pipe(
      map((response) => (response.body || []).find((phone) => !!phone.reference)?.reference ?? null),
      switchMap((refPhoneCompany) =>
        refPhoneCompany
          ? this.workHourApi
              .listWorkHour({ ...this.headers(), refPhoneCompany }, SILENT_HTTP_CONTEXT)
              .pipe(map((response) => response.body || []))
          : of([]),
      ),
    );
  }

  markSoldOut(productReference: string, choice: SoldOutChoice): Observable<unknown> {
    const until = choice.until?.toISOString() ?? null;
    if (choice.salePointReference) {
      return this.markSoldOutAt(choice.salePointReference, productReference, until);
    }
    // The generated toggleInStock has no body yet: same endpoint, with the optional date.
    const url = `${this.erpConfig.rootUrl}/v1/api/products/in-stock/${encodeURIComponent(productReference)}`;
    return this.http.patch(url, until ? { outOfStockUntil: until } : null, { headers: new HttpHeaders(this.headers()) });
  }

  private markSoldOutAt(salePointReference: string, productReference: string, until: string | null): Observable<unknown> {
    return this.salePoints.getMenu(salePointReference).pipe(
      switchMap((menu: MenuWithUntil) => {
        const outOfStock = new Set(menu.outOfStockProductReferences || []);
        outOfStock.add(productReference);
        const dates = { ...(menu.outOfStockUntil || {}) };
        if (until) {
          dates[productReference] = until;
        } else {
          delete dates[productReference];
        }
        const next: MenuWithUntil = { ...menu, outOfStockProductReferences: [...outOfStock], outOfStockUntil: dates };
        return this.salePoints.saveMenu(salePointReference, next);
      }),
    );
  }
}
